/**
 * IndexedDB Service for Offline Data Storage
 * Handles local caching of audits, templates, calendar events
 */

const DB_NAME = 'hospiceguard_offline';
const DB_VERSION = 1;

// Store names
const STORES = {
  AUDITS: 'audits',
  TEMPLATES: 'templates',
  CALENDAR_EVENTS: 'calendar_events',
  OFFLINE_QUEUE: 'offline_queue',
  SYNC_META: 'sync_meta',
  USER_DATA: 'user_data',
};

class IndexedDBService {
  constructor() {
    this.db = null;
    this.isSupported = 'indexedDB' in window;
  }

  async init() {
    if (!this.isSupported) {
      console.warn('IndexedDB not supported');
      return false;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('IndexedDB error:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('IndexedDB initialized');
        resolve(true);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Audits store
        if (!db.objectStoreNames.contains(STORES.AUDITS)) {
          const auditsStore = db.createObjectStore(STORES.AUDITS, { keyPath: 'id' });
          auditsStore.createIndex('user_id', 'user_id', { unique: false });
          auditsStore.createIndex('created_at', 'created_at', { unique: false });
          auditsStore.createIndex('patient_name', 'patient_name', { unique: false });
        }

        // Templates store
        if (!db.objectStoreNames.contains(STORES.TEMPLATES)) {
          const templatesStore = db.createObjectStore(STORES.TEMPLATES, { keyPath: 'id' });
          templatesStore.createIndex('lcd_type', 'lcd_type', { unique: false });
          templatesStore.createIndex('discipline', 'discipline', { unique: false });
        }

        // Calendar events store
        if (!db.objectStoreNames.contains(STORES.CALENDAR_EVENTS)) {
          const eventsStore = db.createObjectStore(STORES.CALENDAR_EVENTS, { keyPath: 'id' });
          eventsStore.createIndex('due_date', 'due_date', { unique: false });
          eventsStore.createIndex('type', 'type', { unique: false });
        }

        // Offline queue for pending actions
        if (!db.objectStoreNames.contains(STORES.OFFLINE_QUEUE)) {
          const queueStore = db.createObjectStore(STORES.OFFLINE_QUEUE, { keyPath: 'id', autoIncrement: true });
          queueStore.createIndex('action', 'action', { unique: false });
          queueStore.createIndex('created_at', 'created_at', { unique: false });
        }

        // Sync metadata
        if (!db.objectStoreNames.contains(STORES.SYNC_META)) {
          db.createObjectStore(STORES.SYNC_META, { keyPath: 'key' });
        }

        // User data cache
        if (!db.objectStoreNames.contains(STORES.USER_DATA)) {
          db.createObjectStore(STORES.USER_DATA, { keyPath: 'key' });
        }
      };
    });
  }

  // Generic CRUD operations
  async put(storeName, data) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.put(data);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async get(storeName, key) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getAll(storeName, indexName = null, query = null) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const target = indexName ? store.index(indexName) : store;
      const request = query ? target.getAll(query) : target.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async delete(storeName, key) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(key);

      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  async clear(storeName) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();

      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  // Specialized methods for offline support
  async cacheAudits(audits) {
    const transaction = this.db.transaction([STORES.AUDITS], 'readwrite');
    const store = transaction.objectStore(STORES.AUDITS);
    
    for (const audit of audits) {
      store.put({ ...audit, cached_at: new Date().toISOString() });
    }

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve(true);
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async getCachedAudits(userId) {
    return this.getAll(STORES.AUDITS, 'user_id', IDBKeyRange.only(userId));
  }

  async cacheTemplates(templates) {
    const transaction = this.db.transaction([STORES.TEMPLATES], 'readwrite');
    const store = transaction.objectStore(STORES.TEMPLATES);
    
    for (const template of templates) {
      store.put({ ...template, cached_at: new Date().toISOString() });
    }

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve(true);
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async getCachedTemplates() {
    return this.getAll(STORES.TEMPLATES);
  }

  async cacheCalendarEvents(events) {
    const transaction = this.db.transaction([STORES.CALENDAR_EVENTS], 'readwrite');
    const store = transaction.objectStore(STORES.CALENDAR_EVENTS);
    
    for (const event of events) {
      store.put({ ...event, cached_at: new Date().toISOString() });
    }

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve(true);
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async getCachedCalendarEvents() {
    return this.getAll(STORES.CALENDAR_EVENTS);
  }

  // Offline queue management
  async queueOfflineAction(action, data, endpoint) {
    const queueItem = {
      action,
      data,
      endpoint,
      created_at: new Date().toISOString(),
      retries: 0,
    };
    return this.put(STORES.OFFLINE_QUEUE, queueItem);
  }

  async getOfflineQueue() {
    return this.getAll(STORES.OFFLINE_QUEUE);
  }

  async removeFromQueue(id) {
    return this.delete(STORES.OFFLINE_QUEUE, id);
  }

  async clearOfflineQueue() {
    return this.clear(STORES.OFFLINE_QUEUE);
  }

  // Sync metadata
  async setLastSync(storeName) {
    return this.put(STORES.SYNC_META, {
      key: `last_sync_${storeName}`,
      timestamp: new Date().toISOString(),
    });
  }

  async getLastSync(storeName) {
    const result = await this.get(STORES.SYNC_META, `last_sync_${storeName}`);
    return result?.timestamp;
  }

  // User preferences
  async setUserPreference(key, value) {
    return this.put(STORES.USER_DATA, { key, value });
  }

  async getUserPreference(key) {
    const result = await this.get(STORES.USER_DATA, key);
    return result?.value;
  }

  // Storage info
  async getStorageEstimate() {
    if (navigator.storage && navigator.storage.estimate) {
      const estimate = await navigator.storage.estimate();
      return {
        usage: estimate.usage,
        quota: estimate.quota,
        percentUsed: ((estimate.usage / estimate.quota) * 100).toFixed(2),
      };
    }
    return null;
  }
}

// Singleton instance
const indexedDBService = new IndexedDBService();

export { indexedDBService, STORES };
export default indexedDBService;
